import {Component, EventEmitter, Input, OnInit, Output, ViewContainerRef} from '@angular/core';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, filter, switchMap, take, tap} from 'rxjs/operators';
import {combineLatest, of} from 'rxjs';

import {TerraUtils} from '../../../TerraUtils';
import {RoutingService} from '../../../../services/routing.service';
import HoldingStatus from '../../../enums/HoldingStatus.enum';
import {UnitBankAccountReqRes} from '../../../models/gp/UnitBankAccountReqRes.model';
import {TransferUsingPlaidDialogComponent, TransferUsingPlaidParams} from '../transfer-using-plaid-dialog/transfer-using-plaid-dialog.component';
import {PlaidOAuth} from '../../../models/OAuth/PlaidOAuth.model';
import {StorageService} from '../../../../services/storage.service';
import {AlertDialogParams} from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import {DialogService} from 'src/app/services/dialog.service';
import {SnackbarService} from 'src/app/services/snackbar.service';
import {UnitApplicationParentType} from 'src/app/shared/enums/unit-bank-account/UnitApplicationParentType.enum';
import {UnitBankAccountService} from '../unit-bank-account/unit-bank-account.service';
import {PlaidService} from 'src/app/services/shared/plaid.service';
import {PlaidDataService} from 'src/app/services/shared/plaid-data.service';
import {UserService} from 'src/app/services/shared/user.service';
import {ConfirmDialogParams} from '../../confirm-dialog/confirm-dialog.component';
import {PermissionService} from '../../../../permission/permission.service';
import PermissionLevel from '../../../../permission/enums/permissionLevel.enum';
import {FinancialPermissionLevel} from '../../../../permission/enums/financialPermissionLevel.enum';
import {GeneralSettingsService} from '../../../../services/shared/general-settings.service';
import UnitDepositAccountStatus from '../../../enums/UnitDepositAccountStatus.enum';
import {CheckDepositDialogData, UnitCheckDepositComponent} from '../../temp/unit-check-deposit/unit-check-deposit.component';
import { Location } from '@angular/common';

@Component({
  selector: 'terra-unit-bank-account-actions',
  templateUrl: './unit-bank-account-actions.component.html',
  styleUrls: ['./unit-bank-account-actions.component.scss'],
  providers: [PlaidService, PlaidDataService]
})
export class UnitBankAccountActionsComponent extends OnDestroyMixin implements OnInit {
  @Input() bankAccount: UnitBankAccountReqRes;
  @Output() plaidSuccess = new EventEmitter();
  @Output() closeAccountSuccess = new EventEmitter();
  @Output() toggleDistributionSuccess = new EventEmitter<boolean>();

  holdingStatus = HoldingStatus;
  unitApplicationParentType = UnitApplicationParentType;
  financialPermissionLevel = FinancialPermissionLevel;

  accountClosed: boolean;
  paths = TerraUtils.consts.paths;

  accountDetails$ = this.userService.accountDetails$;

  allowCloseAccount$ = combineLatest(this.accountDetails$, this.permissionService.checkPermission(PermissionLevel.Editor, FinancialPermissionLevel.AuthorizedSignatory))
    .pipe(
      switchMap(([accountDetails, permissionResult]) => {
        return of(this.isGpBankAccount && this.generalSettingsService.IsCovercyDomain(accountDetails.email) && permissionResult[0]);
      })
    );

  constructor(private routingService: RoutingService,
    private route: ActivatedRoute,
    private location: Location,
    private vcr: ViewContainerRef,
    private dialog: MatDialog,
    private router: Router,
    private plaidService: PlaidService,
    private storageService: StorageService,
    private unitBankAccountService: UnitBankAccountService,
    private dialogService: DialogService,
    private userService: UserService,
    private permissionService: PermissionService,
    private snackbarService: SnackbarService,
    private generalSettingsService: GeneralSettingsService
  ) {
    super();
  }

  ngOnInit(): void {
    const continueOAuthData = this.storageService.getPersistent<PlaidOAuth>(TerraUtils.consts.OAUTH_LOCAL_STORAGE_KEY);
    if (!!continueOAuthData) {
      this.plaidService.handlePlaidOAuthRedirect(this.openFundAccountDialog.bind(this));
    }
    this.accountClosed = this.bankAccount?.status !== UnitDepositAccountStatus.Open;
    this.autoOpenCheckDepositDialog();
  }

  get isGpBankAccount() {
    return this.bankAccount.unitApplication?.parentType !== UnitApplicationParentType.LP;
  }

  /**
   * To show new distribution button
   */
  get showNewDistributionButton(): boolean {
    if (this.bankAccount.unitApplication.parentType === UnitApplicationParentType.Holding) {
      const allowedHoldingStatusesForDistribution = [HoldingStatus.Owned, HoldingStatus.Realized];

      return allowedHoldingStatusesForDistribution.includes(this.bankAccount.unitApplication?.holding?.status);
    } else {
      return false;
    }
  }

  // goToDistribution() {
  //   if (this.bankAccount.unitApplication?.parentType == UnitApplicationParentType.Holding) {
  //     const holdingId = this.bankAccount.unitApplication?.holding?.id;
  //     this.bankAccount.unitApplication?.holding?.discriminator === HoldingDiscriminator.Fund ?
  //       this.router.navigate([this.routingService.createFundDistribution(holdingId)], { queryParams: { account: this.bankAccount.id } }) :
  //       this.router.navigate([this.routingService.createAssetDistribution(holdingId)], { queryParams: { account: this.bankAccount.id } });
  //   }
  // }

  goToPayment() {
    if (this.isGpBankAccount) {
      this.router.navigate([this.routingService.createCrePayment()], {queryParams: {account: this.bankAccount.id}});
    }
    if (!this.isGpBankAccount) {
      this.router.navigate([this.routingService.lpCreateUnitPayment()], {queryParams: {account: this.bankAccount.id}});
    }
  }


  toggleDistributionUsage(event) {
    if (this.bankAccount.hasOngoingDistributions) {
      const alertDialogParams = new AlertDialogParams();
      alertDialogParams.actionLabel = 'Close';
      alertDialogParams.description = `You have non-finalized distributions using this account for distribution that need to be modified`;
      alertDialogParams.title = 'Notice';
      this.dialogService.alertDialog(alertDialogParams);
      event.preventDefault();
    } else {
      this.unitBankAccountService.toggleDistributionUsage()
        .subscribe(result => {
          if (result === true) {
            this.bankAccount.isDistributionEnabled = !this.bankAccount.isDistributionEnabled;
            if (this.bankAccount.isDistributionEnabled) {
              this.snackbarService.showGeneralMessage('Distributions from this account are now available.');
            } else {
              this.snackbarService.showGeneralMessage('Distributions from this account are disabled.');
            }

            this.unitBankAccountService.refreshAccountDetails();
          } else {
            event.preventDefault();
          }
        }, catchError(err => {
          this.snackbarService.showGeneralMessage(err);
          event.preventDefault();
          return of(null);
        }));
    }
  }


  /**
   * Opens fund account dialog
   * @param continueOAuthFlow To continue OAuth flow after redirecting from OAuth provider
   */
  public openFundAccountDialog(continueOAuthFlow: boolean = false) {
    const dialogConfig = new MatDialogConfig<TransferUsingPlaidParams>();
    dialogConfig.disableClose = true;
    dialogConfig.viewContainerRef = this.vcr;
    dialogConfig.data = {
      unitBankAccountId: this.bankAccount.id,
      unitBankAccountName: this.bankAccount.accountNickname,
      holdingId: this.bankAccount.unitApplication?.holdingId,
      continueOAuthFlow,
      displayAchDebitLimits: true,
      lockAmount: false,
      title: 'Fund your account',
      amountStepButtonText: 'FUND ACCOUNT',
      paymentToken: undefined,
      amount: undefined,
      paymentDescription: `Funding account ${this.bankAccount.accountNickname}`,
      paymentAddenda: `Account ${this.bankAccount.accountNickname}`,
      accountHolderDefault: this.bankAccount.unitApplication.name
    };

    this.dialog.open(TransferUsingPlaidDialogComponent, dialogConfig).afterClosed()
      .pipe(
        untilComponentDestroyed(this)
      ).subscribe(_ => {
        this.plaidSuccess.emit();
      });
  }

  public closeAccountDialog(bankAccountId: number) {
    const dialogParams = new ConfirmDialogParams();
    dialogParams.actionLabel = `Proceed`;
    dialogParams.title = `Close this account`;
    dialogParams.description = `<p>Closing an account will also close the cards associated with the account.</p><span>When closing an account with a balance of $1 or less, the funds will automatically be transferred to the your organization's revenue account (see more at Account Low Balance Closure). When closing an account with a balance greater than $1, a check will be mailed to the business or individual address on file. When closing an account with a negative balance, the balance will be considered a loss, and will be covered from your reserve account</span>`;

    const dialogRef = this.dialogService.confirmDialog(dialogParams);
    const errMessage = 'Failed to close the account. Try again';
    dialogRef.afterClosed()
      .pipe(
        take(1),
        untilComponentDestroyed(this),
        filter(confirmed => !!confirmed),
        switchMap(_ => this.unitBankAccountService.closeAccount(bankAccountId))
      )
      .subscribe({
        next: res => {
          if (!!res) {
            this.accountClosed = true;
            this.snackbarService.showGeneralMessage('Account is closed');
            this.closeAccountSuccess.emit();
          } else {
            this.snackbarService.showGeneralMessage(errMessage, 10);
          }
        },
        error: error => {
          this.snackbarService.showGeneralMessage(!!error?.error?.exceptionMessage ? error.error.exceptionMessage : errMessage, 10);
        }
      });
  }
  public downloadFaqPdf(bankAccountId: number) {
    // return;
    this.unitBankAccountService.downloadFaqPdf(bankAccountId)
      .pipe(take(1))
      .subscribe(
        isDownloaded => {
          this.snackbarService.showGeneralMessage('File Downloaded');
        },
        err => {
        }
      );
  }

  public contactCovercy() {

  }

  autoOpenCheckDepositDialog(): void {
    this.route.fragment
    .pipe(
      take(1),
      filter(fragment => !!fragment && fragment.includes('checkDeposit')),
      tap(_ => this.openCheckDepositDialog()),
      tap(_=> this.location.replaceState(this.location.path(false).split('#')[0]))
    ).subscribe();
  }


  public openCheckDepositDialog() {
    const dialogConfig = new MatDialogConfig<CheckDepositDialogData>();
    dialogConfig.viewContainerRef = this.vcr;
    dialogConfig.width = '910px';
    dialogConfig.height = '450px';
    dialogConfig.data = {
      unitBankAccountId: this.bankAccount.unitResourceId.toString(),
      unitCustomerId: this.bankAccount.unitApplication.unitCustomerId.toString()
    };

    this.dialog.open(UnitCheckDepositComponent, dialogConfig);
  }
}
